"use client";

import {useMemo} from "react";
import {useQuery} from "@tanstack/react-query";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsMore from "highcharts/highcharts-more";

import api from "@/lib/api";
import FetchError from "./fetch-error";
import ChartSkeleton from "../home/chart-skeleton";

if (typeof Highcharts === "object") {
  HighchartsMore(Highcharts);
  // Optional: avoid redefining during HMR
  if (!(Highcharts as any).seriesTypes?.column2) {
    Highcharts.seriesType("column2", "column", {});
  }
}

type MonthRange = [number, number];

export type ChartData = {
  dates?: string[];
  regions?: string[];
  series?: Array<{
    name: string;
    data?: Array<MonthRange | null>;
    color?: string;
  }>;
};

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

export default function BasicChart() {
  const {data, error, isPending} = useQuery({
    queryKey: ["statistics", "covid-chart"],
    queryFn: () =>
      api.get<ChartData>("/cms/statistics/column_range/?site_name=covid"),
  });

  // ✅ Safe defaults (no crashes if undefined)
  const dates = Array.isArray(data?.dates) ? data!.dates : [];
  const regions = Array.isArray(data?.regions) ? data!.regions : [];
  const series = Array.isArray(data?.series) ? data!.series : [];
  const yMax = Math.max(0, dates.length - 1);

  // ✅ Hook is always called (even while loading/error)
  const options: Highcharts.Options = useMemo(
    () => ({
      chart: {type: "columnrange", inverted: true, height: 750},
      title: {text: "COVID-19 Interviews: Collection Periods"},

      xAxis: {categories: regions, title: {text: ""}},

      yAxis: {
        min: 0,
        max: yMax,
        tickInterval: 1,
        title: {text: ""},
        labels: {
          formatter: function () {
            const i = clamp(Math.round(this.value as number), 0, yMax);
            return dates[i] ?? "";
          },
        },
      },

      legend: {enabled: true},

      tooltip: {
        shared: false,
        formatter: function () {
          const p = this.point as Highcharts.Point & {
            low?: number;
            high?: number;
          };
          const country = (this.key as string) ?? "";

          const lowRaw = Number(p.low);
          const highRaw = Number(p.high);

          const lowIdx = Number.isFinite(lowRaw)
            ? clamp(Math.round(lowRaw), 0, yMax)
            : 0;
          const highIdx = Number.isFinite(highRaw)
            ? clamp(Math.round(highRaw), 0, yMax)
            : 0;

          const start = dates[lowIdx] ?? "";
          const end = dates[highIdx] ?? "";

          return `<b>${country}</b><br/>${this.series.name}: ${start} \u2192 ${end}`;
        },
      },

      plotOptions: {
        columnrange: {
          grouping: false,
          borderWidth: 0,
          dataLabels: {enabled: false},
        },
      },

      series: series.map((s) => ({
        type: "columnrange",
        name: s?.name ?? "Series",
        color: s?.color,
        data: (Array.isArray(s?.data) ? s!.data! : []).map((rng) => {
          if (!rng) return null;
          const low = Number(rng[0]);
          const high = Number(rng[1]);
          if (!Number.isFinite(low) || !Number.isFinite(high)) return null;
          return {
            low: clamp(Math.round(low), 0, yMax),
            high: clamp(Math.round(high), 0, yMax),
          };
        }),
      })) as Highcharts.SeriesOptionsType[],
    }),
    [dates, regions, series, yMax]
  );

  if (isPending) return <ChartSkeleton />;
  if (error) {
    return (
      <FetchError message={(error as any).message ?? "Failed to load chart"} />
    );
  }

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}
